import { useEffect, useMemo, useRef, useState } from "react";

import type { SMSThreadMessage } from "../queries";
import { isFloridaSmsSendWindow } from "../queries/useSendSmsThread";

const FL_TZ = "America/New_York";

export type IphoneSmsThreadProps = {
  customerName: string;
  phoneMasked: string;
  messages: SMSThreadMessage[];
  hasStop?: boolean;
  quoteId?: string | null;
  isLoading?: boolean;
  isError?: boolean;
  /** IANA timezone for day/time labels. Defaults to Florida ops timezone. */
  timeZone?: string;
  canSend?: boolean;
  isSending?: boolean;
  sendError?: string | null;
  onSend?: (body: string) => Promise<void>;
};

function dayKey(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`));
}

function formatDayLabel(iso: string, timeZone: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const todayKey = fmt.format(new Date());
  const y = new Date();
  y.setDate(y.getDate() - 1);
  const yesterdayKey = fmt.format(y);
  const msgKey = fmt.format(d);
  if (msgKey === todayKey) return "Today";
  if (msgKey === yesterdayKey) return "Yesterday";
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
}

function formatTime(iso: string, timeZone: string): string {
  const d = new Date(iso.includes("T") ? iso : `${iso.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  }).format(d);
}

type DayItem = { type: "day"; label: string; key: string };
type MsgItem = { type: "msg"; msg: SMSThreadMessage; key: string };

function buildItems(messages: SMSThreadMessage[], timeZone: string): Array<DayItem | MsgItem> {
  const items: Array<DayItem | MsgItem> = [];
  let lastDay = "";
  for (const msg of messages) {
    const dk = dayKey(msg.occurred_at, timeZone);
    if (dk !== lastDay) {
      items.push({ type: "day", label: formatDayLabel(msg.occurred_at, timeZone), key: dk });
      lastDay = dk;
    }
    items.push({
      type: "msg",
      msg,
      key: `${msg.message_sid || msg.id}-${msg.occurred_at}`,
    });
  }
  return items;
}

/**
 * Phone-frame SMS thread (Murray Lite iMessage style).
 * Optional composer via onSend — TCPA Florida hours enforced in UI + caller.
 */
export function IphoneSmsThread({
  customerName,
  phoneMasked,
  messages,
  hasStop = false,
  quoteId = null,
  isLoading = false,
  isError = false,
  timeZone = FL_TZ,
  canSend = false,
  isSending = false,
  sendError = null,
  onSend,
}: IphoneSmsThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const items = useMemo(() => buildItems(messages, timeZone), [messages, timeZone]);
  const initials = (customerName || "?").trim().slice(0, 1).toUpperCase() || "?";
  const inHours = isFloridaSmsSendWindow();
  const composerEnabled = Boolean(canSend && onSend && !hasStop);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  return (
    <div
      className="mx-auto flex h-[min(72vh,640px)] w-full max-w-[390px] flex-col overflow-hidden rounded-[28px] border border-neutral-300 bg-[#0a0a0a] shadow-2xl ring-1 ring-black/10"
      data-testid="iphone-sms-thread"
      role="region"
      aria-label={`SMS conversation with ${customerName || "customer"}`}
    >
      <div className="shrink-0 bg-[#1c1c1e]/95 px-4 pb-3 pt-3 backdrop-blur">
        <div className="mb-2 flex items-center justify-center">
          <div className="h-1.5 w-20 rounded-full bg-neutral-700" aria-hidden />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-b from-neutral-500 to-neutral-700 text-lg font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[17px] font-semibold text-white">{customerName || "Customer"}</p>
            <p className="truncate font-mono text-[12px] text-neutral-400">
              {phoneMasked}
              {quoteId ? ` · #${quoteId}` : ""}
              {hasStop ? " · STOP" : ""}
            </p>
          </div>
          {hasStop ? (
            <span className="rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-semibold text-red-300">
              STOP
            </span>
          ) : null}
        </div>
      </div>

      <div className="flex-1 space-y-1 overflow-y-auto bg-black px-3 py-4">
        {isLoading ? (
          <p className="py-12 text-center text-sm text-neutral-500" role="status">
            Loading conversation...
          </p>
        ) : null}
        {isError ? (
          <p className="py-12 text-center text-sm text-red-400" role="status">
            SMS conversation unavailable.
          </p>
        ) : null}
        {!isLoading && !isError && messages.length === 0 ? (
          <p className="py-12 text-center text-sm text-neutral-500" role="status">
            No messages in this thread yet.
          </p>
        ) : null}
        {!isLoading && !isError
          ? items.map((item) => {
              if (item.type === "day") {
                return (
                  <div key={`d-${item.key}`} className="flex justify-center py-3">
                    <span className="rounded-full bg-neutral-800 px-3 py-1 text-[11px] font-medium text-neutral-400">
                      {item.label}
                    </span>
                  </div>
                );
              }
              const m = item.msg;
              const isInbound = m.direction === "inbound";
              const isInternal = m.direction === "internal";
              const out = !isInbound && !isInternal;
              return (
                <div
                  key={item.key}
                  className={`flex ${out ? "justify-end" : isInternal ? "justify-center" : "justify-start"}`}
                  data-testid={`iphone-sms-bubble-${m.direction}`}
                >
                  <div
                    className={`max-w-[78%] px-3.5 py-2 text-[15px] leading-snug ${
                      isInternal
                        ? "rounded-[18px] border border-amber-500/40 bg-amber-950/80 text-amber-50"
                        : out
                          ? "rounded-[18px] rounded-br-[6px] bg-[#0A84FF] text-white"
                          : "rounded-[18px] rounded-bl-[6px] bg-[#262628] text-white"
                    }`}
                  >
                    {isInternal ? (
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-amber-200/90">
                        Internal
                      </p>
                    ) : null}
                    <p className="whitespace-pre-wrap break-words">{m.body}</p>
                    <p
                      className={`mt-1 text-[10px] ${
                        out ? "text-blue-100/80" : isInternal ? "text-amber-200/70" : "text-neutral-400"
                      }`}
                    >
                      {formatTime(m.occurred_at, timeZone)}
                      {m.is_stop ? " · STOP" : ""}
                      {out && m.status === "failed" ? " · failed" : ""}
                      {out && m.status && m.status !== "failed" && m.status !== "delivered"
                        ? ` · ${m.status}`
                        : ""}
                    </p>
                  </div>
                </div>
              );
            })
          : null}
        <div ref={bottomRef} />
      </div>

      <div className="shrink-0 border-t border-neutral-800 bg-[#1c1c1e] px-3 py-3 pb-4">
        {hasStop ? (
          <p className="py-2 text-center text-sm text-red-400" role="status">
            STOP / opt-out — SMS disabled
          </p>
        ) : !composerEnabled ? (
          <p className="py-1 text-center text-[11px] text-neutral-500" role="status">
            Reply unavailable for this thread
          </p>
        ) : (
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const body = draft.trim();
              if (!body || !onSend || isSending) return;
              if (!inHours) {
                setLocalError("SMS only 8:00–20:00 Florida time (closed Sunday).");
                return;
              }
              setLocalError(null);
              void onSend(body)
                .then(() => setDraft(""))
                .catch((err: unknown) => {
                  setLocalError(err instanceof Error ? err.message : "Send failed");
                });
            }}
          >
            <div className="flex min-h-[36px] flex-1 items-center rounded-full border border-neutral-600 bg-[#2c2c2e] px-4 py-2">
              <input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={inHours ? "Text message" : "Outside Florida SMS hours"}
                className="w-full bg-transparent text-[15px] text-white outline-none placeholder:text-neutral-500"
                disabled={isSending || !inHours}
                autoComplete="off"
                maxLength={480}
                data-testid="iphone-sms-composer"
                aria-label="SMS reply"
              />
            </div>
            <button
              type="submit"
              disabled={isSending || !draft.trim() || !inHours}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#0A84FF] text-white disabled:opacity-40"
              aria-label="Send SMS"
              data-testid="iphone-sms-send"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            </button>
          </form>
        )}
        {localError || sendError ? (
          <p className="mt-2 text-center text-xs text-red-400" role="status">
            {localError || sendError}
          </p>
        ) : null}
        {composerEnabled && inHours ? (
          <p className="mt-2 text-center text-[10px] text-neutral-500">
            Florida hours 8:00–20:00 · no Sunday
          </p>
        ) : null}
      </div>
    </div>
  );
}
